import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Heart, Minus, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

import { getMenu, type MenuResponse } from '@/services/client';
import { useCart } from '@/store/cart.store';
import { useFavorites } from '@/store/favorites.store';
import { formatRub } from '@/utils/format';
import { asAbsoluteUrl } from '@/utils/url';

type Dish = MenuResponse['categories'][number]['dishes'][number];

export default function DishDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const estId = useCart((s) => s.establishmentId);
  const add = useCart((s) => s.add);
  const fav = useFavorites((s) => (id ? s.ids.includes(id) : false));
  const toggleFav = useFavorites((s) => s.toggle);

  const { data, isLoading } = useQuery({
    queryKey: ['client/menu', estId],
    queryFn: () => getMenu(estId!),
    enabled: !!estId,
  });

  const dish = useMemo<Dish | null>(() => {
    if (!data || !id) return null;
    for (const c of data.categories) {
      const d = c.dishes.find((x) => x.id === id);
      if (d) return d;
    }
    return null;
  }, [data, id]);

  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);

  if (isLoading) {
    return <div className="p-6 text-center text-slate-400">Загрузка…</div>;
  }
  if (!dish) {
    return (
      <div className="p-6 text-center">
        <div className="text-slate-400 mb-3">Блюдо не найдено</div>
        <button className="btn-outline" onClick={() => navigate('/menu')}>Вернуться в меню</button>
      </div>
    );
  }

  const photos = (dish.photos && dish.photos.length ? dish.photos : dish.photoUrl ? [dish.photoUrl] : [])
    .map((u) => asAbsoluteUrl(u))
    .filter((u): u is string => !!u);

  const chosenMods = dish.modifiers.filter((m) => selected.includes(m.id));
  const extras = chosenMods.reduce((s, m) => s + m.price, 0);
  const unitPrice = dish.price + extras;
  const total = unitPrice * qty;

  const addToCart = () => {
    for (let i = 0; i < qty; i++) {
      add({
        dishId: dish.id,
        name: dish.name,
        price: dish.price,
        photoUrl: dish.photoUrl,
        modifiers: chosenMods.map((m) => ({ id: m.id, name: m.name, price: m.price })),
      });
    }
    toast.success(`${dish.name} · ${qty} шт`);
    navigate(-1);
  };

  return (
    <div className="pb-28">
      {/* Галерея с кнопкой назад и избранным */}
      <div className="relative bg-slate-100">
        {photos.length === 0 ? (
          <div className="aspect-square flex items-center justify-center text-slate-300 text-6xl">🍽</div>
        ) : photos.length === 1 ? (
          <img src={photos[0]} alt={dish.name} className="w-full aspect-square object-cover" />
        ) : (
          <DishSwiper photos={photos} name={dish.name} />
        )}

        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Назад"
          className="absolute top-3 left-3 w-10 h-10 rounded-full bg-white/90 text-slate-700 shadow flex items-center justify-center"
        >
          <ArrowLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => toggleFav(dish.id)}
          aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
          className={`absolute top-3 right-3 w-10 h-10 rounded-full shadow flex items-center justify-center ${
            fav ? 'bg-rose-50 text-rose-500' : 'bg-white/90 text-slate-500'
          }`}
        >
          <Heart size={18} fill={fav ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Описание */}
      <div className="px-4 pt-4">
        <h1 className="text-2xl font-semibold">{dish.name}</h1>
        {dish.weight && <div className="text-sm text-slate-400 mt-0.5">{dish.weight}</div>}
        {dish.description && (
          <p className="text-sm text-slate-600 leading-relaxed mt-3 whitespace-pre-line">
            {dish.description}
          </p>
        )}
      </div>

      {/* Модификаторы */}
      {dish.modifiers.length > 0 && (
        <div className="px-4 mt-5">
          <div className="text-sm font-semibold mb-2">Добавки</div>
          <div className="card p-1 divide-y divide-slate-100">
            {dish.modifiers.map((m) => {
              const checked = selected.includes(m.id);
              return (
                <label
                  key={m.id}
                  className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-brand-600"
                    checked={checked}
                    onChange={(e) => {
                      setSelected(
                        e.target.checked
                          ? [...selected, m.id]
                          : selected.filter((x) => x !== m.id),
                      );
                    }}
                  />
                  <div className="flex-1">{m.name}</div>
                  <div className="text-slate-500 tabular-nums">+{formatRub(m.price)}</div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Количество */}
      <div className="px-4 mt-5 flex items-center gap-3">
        <div className="text-sm font-semibold">Количество</div>
        <div className="ml-auto flex items-center gap-3 bg-white border border-slate-200 rounded-full px-1 py-1">
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 disabled:opacity-40"
            disabled={qty <= 1}
          >
            <Minus size={16} />
          </button>
          <div className="min-w-[28px] text-center font-semibold tabular-nums">{qty}</div>
          <button
            type="button"
            onClick={() => setQty((q) => Math.min(99, q + 1))}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Фиксированная кнопка внизу — поверх всего, чтобы таббар/оверлеи её не перекрывали */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-100 safe-bottom">
        <div className="max-w-lg mx-auto px-4 py-3">
          <button
            className="btn-primary w-full h-12 text-base"
            onClick={addToCart}
          >
            <Plus size={18} /> В корзину · {formatRub(total)}
          </button>
        </div>
      </div>
    </div>
  );
}

function DishSwiper({ photos, name }: { photos: string[]; name: string }) {
  const [idx, setIdx] = useState(0);
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const w = el.clientWidth || 1;
    const next = Math.round(el.scrollLeft / w);
    if (next !== idx && next >= 0 && next < photos.length) setIdx(next);
  };
  return (
    <div className="relative">
      <div
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
      >
        {photos.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={`${name} ${i + 1}`}
            className="w-full aspect-square object-cover shrink-0 snap-center"
          />
        ))}
      </div>
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
        {photos.map((_, i) => (
          <span
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === idx ? 'bg-white' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
